import { SxProps } from '@mui/material/styles'
import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'

type UseLineClampOverflowProps = {
  /** Maximum number of lines before content is clamped. */
  lineClamp: number
  /** When false, skips overflow measurement and line-clamp styling. */
  enabled?: boolean
  /** When true, exposes view more / view less controls for expanding clamped content. */
  canToggle?: boolean
  /** Resets expanded state and re-measures overflow when this value changes. Pass when `canToggle` is true. */
  text?: string | undefined | null
}

export type UseLineClampOverflowReturn<T extends HTMLElement> = {
  /** Attach to the element whose content may overflow. */
  ref: RefObject<T | null>
  /** Whether clamped content exceeds `lineClamp` (only measured while collapsed). */
  isOverflowing: boolean
  /** MUI `sx` props that apply `-webkit-line-clamp` while content is clamped. */
  overflowStyling: SxProps
  viewMore: () => void
  viewLess: () => void
  showViewMore: boolean
  showViewLess: boolean
}

/**
 * Measures text overflow with `-webkit-line-clamp` and optionally supports expand/collapse.
 *
 * Overflow is detected with `scrollHeight > clientHeight` while clamped. A `ResizeObserver`
 * re-checks on element resize; when `canToggle` is true, measurement pauses while expanded.
 *
 * @example Overflow only (e.g. tooltip on hover)
 * const { ref, isOverflowing, overflowStyling } = useLineClampOverflow({
 *   lineClamp: 7,
 *   enabled: shouldClamp,
 * })
 *
 * @example Expandable text
 * const { ref, overflowStyling, showViewMore, viewMore } = useLineClampOverflow({
 *   lineClamp: 13,
 *   canToggle: true,
 *   text: description,
 * })
 */
const useLineClampOverflow = <T extends HTMLElement = HTMLElement>({
  lineClamp,
  enabled = true,
  canToggle = false,
  text,
}: UseLineClampOverflowProps): UseLineClampOverflowReturn<T> => {
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const ref = useRef<T>(null)

  useEffect(() => {
    if (canToggle) {
      setExpanded(false)
    }
  }, [text, canToggle])

  useLayoutEffect(() => {
    const el = ref.current
    const isExpanded = canToggle && expanded

    if (!el || !enabled || isExpanded) {
      setIsOverflowing(false)
      return
    }

    const check = () => setIsOverflowing(el.scrollHeight > el.clientHeight)
    check()

    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, enabled, expanded, lineClamp, canToggle])

  const overflowStyling: SxProps =
    enabled && !(canToggle && expanded)
      ? {
          display: '-webkit-box',
          WebkitLineClamp: lineClamp,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }
      : {}

  return {
    ref,
    isOverflowing,
    overflowStyling,
    viewMore: () => setExpanded(true),
    viewLess: () => setExpanded(false),
    showViewMore: canToggle && !expanded && isOverflowing,
    showViewLess: canToggle && expanded,
  }
}

export default useLineClampOverflow
